import { request } from "../request"
import { api_v1_post_event_performances_path } from "../../routes"

// Types for Event Performance data
export interface EventPerformanceBand {
  band_path: string | null
  cover_image_url: string | null
  id: number
  is_verified: boolean
  nada_url: string | null
  name: string
}

export interface EventPerformance {
  id: number
  performance_order: number
  set_time: string | null
  status: "confirmed" | "pending" | "tentative" | "declined"
  notes: string | null
  band: EventPerformanceBand | null
  pending_band_name: string | null
  pending_band_email: string | null
}

export interface EventPerformancesResponse {
  success: true
  event_performances: EventPerformance[]
}

export interface EventPerformancesErrorResponse {
  success: false
  error: string
}

// Type guard function
function isErrorResponse(
  response: EventPerformancesResponse | EventPerformancesErrorResponse,
): response is EventPerformancesErrorResponse {
  return response.success === false
}

/**
 * Fetch event performances for a specific post
 * @param postId - The ID of the post to fetch performances for
 * @returns Promise<EventPerformancesResponse | EventPerformancesErrorResponse>
 */
export const fetchEventPerformances = async (
  postId: number,
): Promise<EventPerformancesResponse | EventPerformancesErrorResponse> => {
  try {
    const path = api_v1_post_event_performances_path(postId)

    const response = await request({
      queryKey: ["event_performances", { path, options: { method: "GET" } }],
    })

    return response
  } catch (error) {
    console.error("Error fetching event performances:", error)
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error occurred",
    }
  }
}

/**
 * Fetch event performances for a specific post with error handling
 * @param postId - The ID of the post to fetch performances for
 * @returns Promise<EventPerformance[]>
 * @throws Error if the request fails or returns an error response
 */
export const getEventPerformances = async (
  postId: number,
): Promise<EventPerformance[]> => {
  const response = await fetchEventPerformances(postId)

  if (isErrorResponse(response)) {
    throw new Error(response.error || "Failed to fetch event performances")
  }

  return response.event_performances
}

/**
 * Get only confirmed event performances for a post
 * @param postId - The ID of the post to fetch performances for
 * @returns Promise<EventPerformance[]>
 */
export const getConfirmedEventPerformances = async (
  postId: number,
): Promise<EventPerformance[]> => {
  const performances = await getEventPerformances(postId)
  return performances.filter((perf) => perf.status === "confirmed")
}

/**
 * Get only pending event performances for a post
 * @param postId - The ID of the post to fetch performances for
 * @returns Promise<EventPerformance[]>
 */
export const getPendingEventPerformances = async (
  postId: number,
): Promise<EventPerformance[]> => {
  const performances = await getEventPerformances(postId)
  return performances.filter((perf) => perf.status === "pending")
}

/**
 * Get event performances ordered by performance order
 * @param postId - The ID of the post to fetch performances for
 * @returns Promise<EventPerformance[]>
 */
export const getOrderedEventPerformances = async (
  postId: number,
): Promise<EventPerformance[]> => {
  const performances = await getEventPerformances(postId)
  return performances.sort((a, b) => a.performance_order - b.performance_order)
}
